import getLang from 'I18n/utils/getLang';
import { dataFetchingClient, registerQuery, useQuery } from '../../utils/DataFetchingClient';
import { getCachedThenNew, quickFetchOrGet } from '../../utils/ajaxUtils';
import { useMemo } from 'react';
import { BASE_URI_LC } from '../../constants/ApiConstants';
import { useIsPartner } from '../partners';
import { updateCache } from '../../utils/DataFetchingClientCache';
const GET_LESSON = registerQuery({
  fieldName: 'lesson',
  args: ['lessonId', 'language'],

  fetcher({
    lessonId,
    language
  }) {
    return quickFetchOrGet(`/academy/learning-center/v1/lessons/${lessonId}/${language}`);
  }

});
export const GET_ALL_LESSONS = registerQuery({
  fieldName: 'allLessons',
  args: ['language'],

  fetcher({
    language
  }) {
    const lang = language || getLang();
    return getCachedThenNew(`lessons-${lang}`, `${BASE_URI_LC}/lessons/${lang}?fallbackLanguage=en`).then(response => response);
  }

});
export const GET_ALL_PARTNER_LESSONS = registerQuery({
  fieldName: 'allPartnerLessons',
  args: ['language'],

  fetcher({
    language
  }) {
    const lang = language || getLang();
    return quickFetchOrGet(`partner-lessons-${lang}`, `${BASE_URI_LC}/lessons/partners/${lang}?fallbackLanguage=en`);
  }

});
export const updateFullLessonCache = (lessonData, language) => {
  updateCache(GET_LESSON, () => lessonData, {
    language,
    lessonId: lessonData.id
  });
};
export const updatePartialLessonCache = (lessonData, language = getLang()) => {
  updateCache(GET_ALL_LESSONS, allLessonsCache => {
    if (!allLessonsCache.result) {
      return {
        result: [lessonData],
        timestamp: new Date().getTime()
      };
    }

    const existingLessonIdx = allLessonsCache.result.findIndex(cachedLesson => cachedLesson.id === lessonData.id);

    if (existingLessonIdx > -1) {
      allLessonsCache.result[existingLessonIdx] = lessonData;
    }

    allLessonsCache.result.push(lessonData);
    return allLessonsCache;
  }, {
    language: language.toLowerCase()
  });
};
export const useFullLesson = (lessonId, language = getLang(), skip) => {
  if (!language || !language.length) language = getLang();
  const {
    loading: lessonLoading,
    data: lesson,
    error: lessonError
  } = useQuery(GET_LESSON, {
    variables: {
      lessonId,
      language
    },
    skip
  });
  return {
    lesson,
    lessonLoading,
    lessonError
  };
};
export const getFullLesson = (lessonId, language = getLang()) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_LESSON,
    variables: {
      lessonId,
      language
    }
  });
};
export const useAllLessons = (language = getLang(), skip = false) => {
  const isPartner = useIsPartner();
  const {
    data: allLessons,
    loading: allLessonsLoading,
    error: allLessonsError
  } = useQuery(GET_ALL_LESSONS, {
    variables: {
      language
    },
    skip
  });
  const {
    data: allPartnerLessons,
    loading: allPartnerLessonsLoading,
    error: allPartnerLessonsError
  } = useQuery(GET_ALL_PARTNER_LESSONS, {
    variables: {
      language
    },
    skip: !isPartner || skip
  });
  const allLessonsRecord = useMemo(() => {
    return [...(Array.isArray(allPartnerLessons) ? allPartnerLessons : []), ...(Array.isArray(allLessons && allLessons.result) ? allLessons.result : [])].reduce((currentLessons, lesson) => {
      currentLessons[lesson.id] = lesson;
      return currentLessons;
    }, {});
  }, [allLessons, allPartnerLessons]);
  return {
    allLessons: allLessonsRecord,
    allLessonsLoading: allLessonsLoading || allPartnerLessonsLoading,
    allLessonsError: allLessonsError || allPartnerLessonsError
  };
};
export const getAllLessons = (language = getLang()) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_LESSONS,
    variables: {
      language
    }
  });
};
export const getAllPartnerLessons = (language = getLang()) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_PARTNER_LESSONS,
    variables: {
      language
    }
  });
};
export const useLesson = (lessonId, language = getLang(), skip = false) => {
  if (!language || !language.length) language = getLang();
  const {
    allLessons,
    allLessonsLoading: lessonLoading,
    allLessonsError: lessonError
  } = useAllLessons(language, skip);
  return {
    lesson: allLessons && allLessons[lessonId],
    lessonLoading,
    lessonError
  };
};
export const useLessonContentById = ({
  lessonId,
  contentId,
  language
}) => {
  const {
    lesson
  } = useFullLesson(lessonId, language, !lessonId);
  if (!lesson) return null;
  return lesson.content.find(content => content.id === contentId);
};